import React, { useEffect, useRef, useState } from 'react';

import { CinemaRoomDto } from '@cineamo/api/models/cinema-room/CinemaRoomDto.types';

import { getCinemaRoomImages } from '@cineamo/api/resources/cinema-room/cinema-room-api';

import { useGetAllListItems } from '@cineamo/ui/src/hooks/useGetAllListItems';

import ImageGalleryContent from '@cineamo/ui/src/image/ImageGalleryContent';

const CinemaRoomImages: React.FC<{ cinemaRoom: CinemaRoomDto }> = ({ cinemaRoom }) => {
    const selectedImageRef = useRef<HTMLImageElement>(null);
    const { data: cinemaRoomImagesData } = useGetAllListItems(
        (page) => getCinemaRoomImages(cinemaRoom.id, { page }),
        'cinema-room-images',
        ['cinema-room-images', `cinemaRoomId=${cinemaRoom.id}`]
    );

    // Images are a single ordered list (first = profile image). Use the relations as the source of
    // truth; fall back to the derived profileImageUrl only for legacy rooms that have no relations yet.
    const relationImageUrls = (cinemaRoomImagesData?.map((image) => image.imageUrl) ?? []).filter(
        (url): url is string => !!url
    );
    const cinemaRoomImages = (relationImageUrls.length > 0 ? relationImageUrls : [cinemaRoom.profileImageUrl]).filter(
        (url): url is string => !!url
    );

    const [selectedCinemaRoomImageIndex, setSelectedCinemaRoomImageIndex] = useState<number>(0);
    useEffect(() => {
        if (cinemaRoomImages?.length && !selectedCinemaRoomImageIndex) {
            // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: reset selection to first image when images load
            setSelectedCinemaRoomImageIndex(0);
        }
    }, [cinemaRoomImages, selectedCinemaRoomImageIndex]);

    useEffect(() => {
        if (selectedImageRef.current) {
            selectedImageRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        }
    }, [selectedCinemaRoomImageIndex]);

    if (cinemaRoomImages.length <= 0) {
        return null;
    }

    return (
        <div className="w-full h-[360px] rounded-4 overflow-hidden relative">
            <ImageGalleryContent
                images={cinemaRoomImages}
                arrowsOverlaying
                roundedCorners
            />
        </div>
    );
};

export default CinemaRoomImages;
